import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaWallet,
  FaUniversity,
  FaStore,
  FaShoppingCart,
  FaLandmark,
  FaBriefcase,
} from "react-icons/fa";

import Header from "../Templete/Header";
import Footer from "../Templete/Footer";
import "./Industries.css";

const industries = [
  {
    id: "fintech",
    title: "Fintech & Payments",
    label: "CONNECTED PAYMENT EXPERIENCES",
    icon: FaWallet,
    description:
      "Payment platforms need clear customer journeys and reliable connections between services. We help businesses develop applications and integrate payment workflows around their operational requirements.",
    solutions: [
      "Payment integration and transaction workflows",
      "Partner and merchant management portals",
      "Transaction reporting and reconciliation tools",
      "API infrastructure and orchestration development",
    ],
    outcome:
      "Bring payment journeys, operational tools, and service integrations into a connected application.",
  },
  {
    id: "banking-finance",
    title: "Banking & Financial Services",
    label: "DIGITAL WORKFLOWS FOR FINANCIAL SERVICES",
    icon: FaUniversity,
    description:
      "We support technology requirements for financial service businesses and banking service partners. Our focus is on applications that connect customer interactions, partner operations, and authorised service integrations.",
    solutions: [
      "Customer and partner onboarding interfaces",
      "Banking correspondent service applications",
      "Integration with authorised banking services",
      "Operational dashboards and reporting workflows",
    ],
    outcome:
      "Give teams clearer visibility across customer requests, service activity, and partner operations.",
  },
  {
    id: "retail",
    title: "Retail & Distribution",
    label: "SIMPLER EVERYDAY OPERATIONS",
    icon: FaStore,
    description:
      "Retailers and distribution businesses manage customers, inventory, orders, and multiple sales channels. We build practical tools that bring these activities together and reduce repetitive work.",
    solutions: [
      "Inventory and order management applications",
      "Retailer and distributor portals",
      "Billing and payment integrations",
      "Sales dashboards and customer management",
    ],
    outcome:
      "Connect sales activity with everyday business operations through a more consistent workflow.",
  },
  {
    id: "e-commerce",
    title: "E-Commerce",
    label: "FROM DISCOVERY TO CHECKOUT",
    icon: FaShoppingCart,
    description:
      "An online store needs more than a product catalogue. We help businesses create shopping experiences that support product discovery, checkout, order management, and customer communication.",
    solutions: [
      "Responsive storefronts and mobile applications",
      "Product catalogue and order management",
      "Payment gateway integrations",
      "Customer accounts and order tracking interfaces",
    ],
    outcome:
      "Create a clearer shopping journey backed by the tools needed to manage online orders.",
  },
  {
    id: "e-governance",
    title: "E-Governance & Citizen Services",
    label: "ACCESSIBLE DIGITAL SERVICES",
    icon: FaLandmark,
    description:
      "Digital service platforms should make application journeys understandable for customers and service operators. We develop interfaces and workflows for document-based services and service centre operations.",
    solutions: [
      "Application and document collection workflows",
      "Service centre and operator dashboards",
      "Application status tracking interfaces",
      "Service integrations and activity reporting",
    ],
    outcome:
      "Make service requests easier to submit, manage, and follow through each stage.",
  },
  {
    id: "business-services",
    title: "Business & Professional Services",
    label: "SOFTWARE THAT FITS YOUR PROCESS",
    icon: FaBriefcase,
    description:
      "Businesses often outgrow spreadsheets and disconnected tools. We develop custom applications that reflect how teams handle enquiries, customers, projects, and internal approvals.",
    solutions: [
      "Business websites and customer portals",
      "CRM and lead management applications",
      "Internal workflow and approval systems",
      "Management dashboards and reporting tools",
    ],
    outcome:
      "Bring important business information and recurring processes into one practical workspace.",
  },
];

const approach = [
  {
    number: "01",
    title: "Understand your workflow",
    description:
      "We start with your users, business processes, and the systems you already use.",
  },
  {
    number: "02",
    title: "Define the solution",
    description:
      "We map the required features, integrations, and user journeys around your priorities.",
  },
  {
    number: "03",
    title: "Build and validate",
    description:
      "We develop the solution and check it against the agreed functional requirements.",
  },
  {
    number: "04",
    title: "Plan for ongoing use",
    description:
      "We consider deployment, maintenance, and future improvements as part of the project.",
  },
];

const Industries = () => {
  useEffect(() => {
    document.title = "Industries We Serve | Formax IT Solutions Pvt. Ltd.";
  }, []);

  return (
    <>
      <Header />

      <main className="fit-industries">
        {/* Hero */}
        <section className="fit-industries__hero">
          <div className="fit-industries__container">
            <nav className="fit-industries__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Industries</span>
            </nav>

            <div className="fit-industries__hero-grid">
              <div>
                <span className="fit-industries__eyebrow">
                  INDUSTRIES WE SERVE
                </span>

                <h1>
                  Different industries.
                  <span>Purpose-built solutions.</span>
                </h1>

                <p>
                  We bring software development, digital services, and payment
                  technology together to address the way your business works.
                </p>

                <a
                  href="#industry-directory"
                  className="fit-industries__button"
                >
                  Explore industries
                  <FaArrowRight aria-hidden="true" />
                </a>
              </div>

              <aside className="fit-industries__hero-card">
                <span className="fit-industries__eyebrow">
                  BUILT AROUND YOUR REQUIREMENTS
                </span>
                <h2>Your industry shapes the solution.</h2>
                <p>
                  Customer expectations, operational processes, and integration
                  needs differ across businesses. We use these requirements to
                  guide what we build.
                </p>

                <ul>
                  <li>
                    <FaCheck aria-hidden="true" />
                    Customer-facing applications
                  </li>
                  <li>
                    <FaCheck aria-hidden="true" />
                    Connected service integrations
                  </li>
                  <li>
                    <FaCheck aria-hidden="true" />
                    Internal business tools
                  </li>
                </ul>
              </aside>
            </div>
          </div>
        </section>

        {/* Industry directory */}
        <section
          id="industry-directory"
          className="fit-industries__section"
          aria-labelledby="industry-directory-title"
        >
          <div className="fit-industries__container">
            <div className="fit-industries__heading">
              <span className="fit-industries__eyebrow">
                EXPLORE OUR FOCUS AREAS
              </span>
              <h2 id="industry-directory-title">
                Find the right starting point for your business.
              </h2>
              <p>
                Explore the applications and workflows we can help develop for
                your industry.
              </p>
            </div>

            <nav aria-label="Industry sections">
              <ul className="fit-industries__directory">
                {industries.map((industry) => {
                  const Icon = industry.icon;

                  return (
                    <li key={industry.id}>
                      <a href={`#${industry.id}`}>
                        <span className="fit-industries__directory-icon">
                          <Icon aria-hidden="true" />
                        </span>
                        <span>{industry.title}</span>
                        <FaArrowRight
                          className="fit-industries__directory-arrow"
                          aria-hidden="true"
                        />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </section>

        {/* Detailed industry sections */}
        <section
          className="fit-industries__section fit-industries__section--soft"
          aria-labelledby="industry-solutions-title"
        >
          <div className="fit-industries__container">
            <div className="fit-industries__heading">
              <span className="fit-industries__eyebrow">
                INDUSTRY SOLUTIONS
              </span>
              <h2 id="industry-solutions-title">
                Technology that fits the work you do.
              </h2>
            </div>

            <div className="fit-industries__details">
              {industries.map((industry) => {
                const Icon = industry.icon;

                return (
                  <article
                    id={industry.id}
                    key={industry.id}
                    className="fit-industries__industry"
                    aria-labelledby={`${industry.id}-title`}
                  >
                    <div className="fit-industries__industry-copy">
                      <span className="fit-industries__icon">
                        <Icon aria-hidden="true" />
                      </span>

                      <span className="fit-industries__eyebrow">
                        {industry.label}
                      </span>

                      <h3 id={`${industry.id}-title`}>{industry.title}</h3>

                      <p>{industry.description}</p>

                      <Link
                        to="/contact-us"
                        className="fit-industries__text-link"
                      >
                        Talk to our team
                        <FaArrowRight aria-hidden="true" />
                      </Link>
                    </div>

                    <div className="fit-industries__solutions">
                      <h4>What we can help build</h4>

                      <ul>
                        {industry.solutions.map((solution) => (
                          <li key={solution}>
                            <FaCheck aria-hidden="true" />
                            <span>{solution}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="fit-industries__outcome">
                        <span>THE FOCUS</span>
                        <p>{industry.outcome}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Delivery approach */}
        <section
          className="fit-industries__section"
          aria-labelledby="industry-approach-title"
        >
          <div className="fit-industries__container">
            <div className="fit-industries__heading">
              <span className="fit-industries__eyebrow">OUR APPROACH</span>
              <h2 id="industry-approach-title">
                Start with the business. Build with clarity.
              </h2>
              <p>
                Our approach connects industry requirements with a defined scope
                and a practical development process.
              </p>
            </div>

            <ol className="fit-industries__process">
              {approach.map((step) => (
                <li key={step.number}>
                  <span aria-hidden="true">{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Contact */}
        <section className="fit-industries__section fit-industries__section--soft">
          <div className="fit-industries__container">
            <div className="fit-industries__cta">
              <div>
                <span className="fit-industries__eyebrow">LET’S CONNECT</span>
                <h2>Your requirements are the starting point.</h2>
                <p>
                  Tell us about your industry, existing systems, and what you
                  want to improve. We’ll help define the next steps.
                </p>
              </div>

              <Link to="/contact-us" className="fit-industries__button">
                Contact us
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Industries;
